import { faArrowCircleRight } from '@fortawesome/pro-solid-svg-icons';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { glossary } from '@sorare/core/src/lib/glossary';

const WIDTH = 250;

const Wrapper = styled.div<{ disabled: boolean }>`
  --border-size: 2px;
  position: relative;
  width: ${WIDTH}px;
  color: ${({ disabled }) =>
    disabled ? `var(--c-nd-600-opaque)` : `var(--c-brand-600)`};
  font-size: var(--t-16);
  font-weight: 700;
  font-family: inherit;
  isolation: isolate;
  box-sizing: border-box;
  border-radius: var(--unit);
  padding: 2px;
  background: rgba(0, 0, 0, 0.05);
  box-shadow:
    inset 0 0 0 var(--border-size) rgba(0, 0, 0, 0.1),
    inset 0 calc(-1 * var(--border-size)) 0 rgba(255, 255, 255, 0.5);
`;

const SlidingWindow = styled.div`
  overflow: hidden;
  border-radius: 6px;
  user-select: none;
  padding: 2px;
`;
const Message = styled(Horizontal).attrs({ gap: 0, center: true })`
  height: 100%;
  width: calc(100% - 8px);
  text-align: center;
  font-size: var(--t-16);
  flex: 1;
  color: var(--c-neutral-1000);
`;

const Before = styled(animated.div)`
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  inset: 0;
`;

const Pushable = styled.button`
  position: relative;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  border-radius: var(--unit);
  cursor: pointer;
  outline-offset: 4px;
  transition: filter 250ms;
  font: inherit;
  .shadow {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: var(--unit);
    background: hsl(0deg 0% 0% / 0.25);
    will-change: transform;
    transform: translateY(2px);
    transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
  }
  .edge {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: var(--unit);
    background: #ddd;
  }
  .front {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    height: 40px;
    font: inherit;
    line-height: 40px;
    border-radius: var(--unit);
    color: currentColor;
    background: #f6f6f6;
    will-change: transform;
    transform: translateY(-4px);
    transition: transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1);
  }
  &:hover {
    filter: brightness(110%);
    .front {
      transform: translateY(-6px);
      transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
    }
    .shadow {
      transform: translateY(4px);
      transition: transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5);
    }
  }
  &:active {
    .front {
      transform: translateY(-2px);
      transition: transform 34ms;
    }

    .shadow {
      transform: translateY(1px);
      transition: transform 34ms;
    }
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const Slider = styled(animated.div)`
  height: 100%;
  display: flex;
  align-items: center;
`;

const SlidingButton = styled(animated.div)`
  border-radius: var(--half-unit);
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
  margin: 0;
  color: currentColor;
  background: var(--c-neutral-1000);
  box-shadow:
    1px 1px 2px 0px rgba(255, 255, 255, 0.3) inset,
    -1px -1px 2px 0px rgba(111, 111, 111, 0.5) inset;
  text-align: center;
  user-select: none;
  aspect-ratio: 1;
  width: calc(5 * var(--unit));
  padding: 0;
  cursor: move;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 1);

  &:disabled {
    background: linear-gradient(0deg, #5a5858, #858585);
  }
  &.availableSoon {
    cursor: not-allowed;
  }
`;

type Props = {
  disabled?: boolean;
  onCraft: () => Promise<null | (() => void)>;
  availableSoon?: boolean;
};
export const CraftingButton = ({
  disabled = false,
  onCraft,
  availableSoon,
}: Props) => {
  const { formatMessage } = useIntl();
  const triggered = useRef(false);
  const [mutationDone, setMutationDone] = useState(false);
  const revealFunction = useRef<(() => void) | null>(null);

  const [{ x }, api] = useSpring(() => ({
    x: 0,
  }));

  const bind = useDrag(({ event, active, movement: [mouseX] }) => {
    event.preventDefault();
    if (!disabled) {
      if (mouseX > WIDTH && !triggered.current && !active) {
        triggered.current = true;
        onCraft().then(playFunction => {
          if (playFunction === null) {
            triggered.current = false;
            api.start({
              x: 0,
            });
          } else {
            setMutationDone(true);
            revealFunction.current = playFunction;
          }
        });
      }
      api.start({
        x: Math.min(
          // can't go too far
          WIDTH,
          Math.max(
            // can't go back once triggered, and prevent dragging left
            triggered.current ? WIDTH : 0,
            // mouse drag
            active ? mouseX : 0
          )
        ),
      });
    }
  });

  return (
    <Wrapper disabled={disabled && !(mutationDone && revealFunction.current)}>
      {mutationDone && revealFunction.current && (
        <Pushable onClick={revealFunction.current}>
          <span className="shadow" />
          <span className="edge" />
          <span className="front">
            <FormattedMessage
              id="CraftingButton.CTA"
              defaultMessage="Reveal my card!"
            />
          </span>
        </Pushable>
      )}
      {!mutationDone && (
        <>
          <Before
            style={{
              clipPath: x.to(t => `rect(0 ${t + 4}px 100% 0)`),
            }}
          >
            <Message>
              {triggered.current ? (
                <Horizontal>
                  <LoadingIndicator
                    small
                    style={{ flex: 'none', width: 'min-content' }}
                  />
                  <FormattedMessage
                    id="CraftingButten.loading"
                    defaultMessage="Pumping essence"
                  />
                </Horizontal>
              ) : (
                <FormattedMessage
                  id="CraftingButton.warning"
                  defaultMessage="No going back…"
                />
              )}
            </Message>
          </Before>
          <SlidingWindow>
            <Slider style={{ x }}>
              <SlidingButton
                className={classNames({ availableSoon })}
                {...bind()}
                aria-label={formatMessage({
                  id: 'CraftingButton.handle',
                  defaultMessage: 'Slider',
                })}
              >
                <FontAwesomeIcon icon={faArrowCircleRight} />
              </SlidingButton>
              <Message>
                {disabled && !availableSoon && (
                  <FormattedMessage
                    id="CraftingButton.disabled"
                    defaultMessage="Not enough essence yet"
                  />
                )}
                {!disabled && !availableSoon && (
                  <FormattedMessage
                    id="CraftingButton.help"
                    defaultMessage="Slide to craft"
                  />
                )}
                {availableSoon && <FormattedMessage {...glossary.comingSoon} />}
              </Message>
            </Slider>
          </SlidingWindow>
        </>
      )}
    </Wrapper>
  );
};
